import * as Yup from 'yup';
import { Task, TaskGroup, TaskFormData } from './task.types';

export const generateInitialValues = (taskGroup?: TaskGroup) => {
  const initialValues: any = {};

  if (!taskGroup || !Array.isArray(taskGroup.tasks)) {
    return initialValues;
  }

  taskGroup.tasks.forEach((task) => {
    const taskKey = `task_${task?.taskId}`;
    initialValues[taskKey] = {};

    // Status field (populate from taskResult if available)
    if (task?.statusConfirmationRequired && task?.taskStatuses?.length > 0) {
      initialValues[taskKey][task?.statusKey] = task?.taskResult || '';
    }

    // Description field
    if (task?.isDescriptionMandatory || (task?.mandatoryDescriptionStatuses?.length > 0 || task?.currentDescription)) {
      initialValues[taskKey][task?.descriptionKey] = task?.currentDescription || '';
    }

    // Image field
    if (task?.isPictureMandatory || (task?.mandatoryPictureStatuses?.length > 0)) {
      initialValues[taskKey][task?.imageFilePathKey] = [];
    }
    if (task?.currentTaskImagePath?.length) {
      initialValues[taskKey][task?.imageFilePathKey] = [...task.currentTaskImagePath];
    }


    // Tyre specific fields
    if (task?.taskType === 'Tyre') {
      if (task?.tyreBrandKey)
        initialValues[taskKey][task?.tyreBrandKey] = task?.currentTyreBrand || '';
      if (task?.tyreSizeKey)
        initialValues[taskKey][task?.tyreSizeKey] = task?.currentTyreSize || '';
      if (task?.tyreDepthKey)
        initialValues[taskKey][task?.tyreDepthKey] = task?.currentTyreDepth || '';
      if (task?.tyreConditionKey)
        initialValues[taskKey][task?.tyreConditionKey] = task?.currentTyreCondition || '';
      if (task?.tyreStatusesKey)
        initialValues[taskKey][task?.tyreStatusesKey] = task?.currentTyreStatuses || '';
    }

    // Light specific fields
    if (task?.taskType === 'Light') {
      if (task?.lightTypesKey)
        initialValues[taskKey][task?.lightTypesKey] = task?.currentLightType || '';
      if (task?.isLightCountRequired && task?.lightCountKey) {
        initialValues[taskKey][task?.lightCountKey] = task?.currentLightCount || '';
      }
    }

    // Reefer specific fields
    if (task?.taskType === 'Reefer') {
      if (task?.presetValueRequired && task?.presetValueKey) {
        initialValues[taskKey][task?.presetValueKey] = task?.currentpresetValue || '';
      }
      if (task?.tempratureValueReuired && task?.tempratureValueKey) {
        initialValues[taskKey][task?.tempratureValueKey] = task?.currenTempratureValue || '';
      }
      if (task?.hoursValueReuired && task?.hoursValueKey) {
        initialValues[taskKey][task?.hoursValueKey] = task?.currenHoursValue || '';
      }
    }
  });

  return initialValues;
};


export const generateValidationSchema = (taskGroup?: TaskGroup) => {
  const taskSchemas: any = {};

  if (!taskGroup || !Array.isArray(taskGroup.tasks)) {
    return Yup.object().shape({});
  }

  taskGroup.tasks.forEach((task) => {
    const taskKey = `task_${task?.taskId}`;
    const fieldValidations: any = {};

    // Status validation
    if (task?.statusConfirmationRequired && task?.taskStatuses?.length > 0) {
      fieldValidations[task?.statusKey] = Yup.string().required(`status is required`);
    }

    // Description validation
    if (task?.isDescriptionMandatory) {
      fieldValidations[task?.descriptionKey] = Yup.string().required(`description is required`);
    } else if (task?.mandatoryDescriptionStatuses?.length > 0) {
      fieldValidations[task?.descriptionKey] = Yup.string().when(task?.statusKey, {
        is: (status: string) => task?.mandatoryDescriptionStatuses.includes(status),
        then: (schema) =>
          schema.required(
            `Description is required when status is ${task?.mandatoryDescriptionStatuses.join(' or ')}`
          ),
        otherwise: (schema) => schema,
      });
    }

    if (task?.isPictureMandatory) {
      if (task?.isMultiPicture) {
        fieldValidations[task?.imageFilePathKey] = Yup.array().min(1, `At least one image is required`);
      } else {
        fieldValidations[task?.imageFilePathKey] = Yup.string().required(`Image is required`);
      }
    }

    // Image validation
    if (task?.isPictureMandatory) {
      fieldValidations[task?.imageFilePathKey] = Yup.array()
        .of(Yup.string().required())
        .min(1, 'At least one image is required');
    } else if (task?.mandatoryPictureStatuses?.length > 0) {
      fieldValidations[task?.imageFilePathKey] = Yup.array()
        .of(Yup.string().required())
        .when(task?.statusKey, {
          is: (status: string) => task?.mandatoryPictureStatuses.includes(status),
          then: (schema) => schema.min(1, `Image is required when status is ${task?.mandatoryPictureStatuses.join(' or ')}`),
          otherwise: (schema) => schema,
        });
    }


    // Tyre specific validations
    if (task?.taskType === 'Tyre') {
      if (task?.tyreBrandKey) {
        fieldValidations[task?.tyreBrandKey] = Yup.string().required('Tyre brand is required');
      }
      if (task?.tyreSizeKey) {
        fieldValidations[task?.tyreSizeKey] = Yup.string().required('Tyre size is required');
      }
      if (task?.tyreDepthKey) {
        fieldValidations[task?.tyreDepthKey] = Yup.string().required('Tyre depth is required');
      }
      if (task?.tyreConditionKey) {
        fieldValidations[task?.tyreConditionKey] = Yup.string().required('Tyre condition is required');
      }
      if (task?.tyreStatusesKey) {
        fieldValidations[task?.tyreStatusesKey] = Yup.string().required('Tyre status is required');
      }
    }

    // Light specific validations
    if (task?.taskType === 'Light') {
      if (task?.lightTypesKey && (task?.lightTypes?.length ?? 0) > 0) {
        fieldValidations[task?.lightTypesKey] = Yup.string().required('Light type is required');
      }
      if (task?.isLightCountRequired && task?.lightCountKey) {
        fieldValidations[task?.lightCountKey] = Yup.number()
          .required('Light count is required')
          .min(1, 'Light count must be at least 1');
      }
    }

    // Reefer specific validations
    if (task?.taskType === 'Reefer') {
      if (task?.presetValueRequired && task?.presetValueKey) {
        fieldValidations[task?.presetValueKey] = Yup.string().required('Preset value is required');
      }
      if (task?.tempratureValueReuired && task?.tempratureValueKey) {
        fieldValidations[task?.tempratureValueKey] = Yup.number()
          .required('Temperature value is required')
          .typeError('Temperature must be a number');
      }
      if (task?.hoursValueReuired && task?.hoursValueKey) {
        fieldValidations[task?.hoursValueKey] = Yup.number()
          .required('Hours value is required')
          .min(0, 'Hours cannot be negative')
          .typeError('Hours must be a number');
      }
    }

    taskSchemas[taskKey] = Yup.object().shape(fieldValidations);
  });

  return Yup.object().shape(taskSchemas);
};
